/**
 * English Writing Data Service - Unified Data Layer
 * 
 * Provides a unified interface for writing tools data with feature flags.
 * Supports gradual migration from Supabase to A2A backend.
 * 
 * Feature Flags:
 * - USE_A2A_BACKEND: Enable A2A backend (default: true)
 * - FALLBACK_TO_SUPABASE: Fallback to Supabase on A2A failure (default: true)
 */

import { FeatureFlags } from '../utils/featureFlags';
import a2aEnglishWritingService from '../api/services/a2aEnglishService';

// Import existing Supabase services
import * as supabaseServices from './supabaseService';

/**
 * English Writing Data Service Class
 */
class EnglishWritingDataService {
  constructor() {
    this.useA2A = FeatureFlags.USE_A2A_BACKEND;
    this.fallbackToSupabase = FeatureFlags.FALLBACK_TO_SUPABASE;
    this.enableLogging = FeatureFlags.ENABLE_LOGGING;
    
    // Always log initialization (even in production) for debugging
    console.log('[EnglishWritingDataService] Initialized', {
      useA2A: this.useA2A,
      fallbackToSupabase: this.fallbackToSupabase,
      featureFlagValue: FeatureFlags.USE_A2A_BACKEND,
      envValue: process.env.REACT_APP_USE_A2A_BACKEND
    });
    
    this.log('EnglishWritingDataService initialized', {
      useA2A: this.useA2A,
      fallbackToSupabase: this.fallbackToSupabase
    });
  }

  /**
   * Internal logging method
   */
  log(message, data = null) {
    if (this.enableLogging) {
      console.log(`[EnglishWritingDataService] ${message}`, data || '');
    }
  }

  /**
   * Internal error logging method
   */
  logError(message, error) {
    console.error(`[EnglishWritingDataService] ${message}`, error);
    if (error && typeof error === 'object') {
      console.error(`[EnglishWritingDataService] Error details:`, {
        message: error.message,
        name: error.name,
        code: error.code
      });
    }
  }

  /**
   * Try A2A with optional Supabase fallback
   */
  async tryWithFallback(a2aMethod, supabaseFallback, methodName) {
    // Try A2A first if enabled
    if (this.useA2A) {
      try {
        console.log(`[EnglishWritingDataService] ${methodName}: Using A2A backend`);
        this.log(`${methodName}: Using A2A backend`);
        const result = await a2aMethod();
        console.log(`[EnglishWritingDataService] ${methodName}: A2A success`);
        this.log(`${methodName}: A2A success`);
        return result;
      } catch (error) {
        const errorDetails = {
          error: error.message,
          name: error.name,
          code: error.code,
          isCapabilityNotFound: error.message && error.message.includes('CAPABILITY_NOT_FOUND'),
          isNetworkError: error.message && (
            error.message.includes('Failed to fetch') ||
            error.message.includes('ERR_CONNECTION_REFUSED') ||
            error.message.includes('NETWORK_ERROR') ||
            error.message.includes('Invalid URL') ||
            error.message.includes('CORS') ||
            error.message.includes('NetworkError')
          )
        };
        
        console.error(`[EnglishWritingDataService] ${methodName}: A2A call failed`, errorDetails);
        
        if (errorDetails.isCapabilityNotFound) {
          try {
            const capabilities = a2aEnglishWritingService.client.getCapabilities();
            if (capabilities && Array.isArray(capabilities)) {
              const capabilityNames = capabilities.map(c => c.name || c).slice(0, 20);
              console.warn(`[EnglishWritingDataService] Available A2A capabilities (first 20):`, capabilityNames);
            }
          } catch (capError) {
            console.warn(`[EnglishWritingDataService] Could not retrieve capabilities:`, capError.message);
          }
        }
        
        const isConnectionError = errorDetails.isNetworkError;
        const fallbackAvailable = this.fallbackToSupabase && supabaseFallback;
        
        if (!isConnectionError || !fallbackAvailable) {
          this.logError(`${methodName}: A2A failed`, error);
        } else {
          console.warn(`[EnglishWritingDataService] ${methodName}: A2A backend error (${error.message}), using Supabase fallback`);
        }
        
        // Fallback to Supabase if enabled
        if (fallbackAvailable) {
          console.log(`[EnglishWritingDataService] ${methodName}: Falling back to Supabase`);
          try {
            const result = await supabaseFallback();
            console.log(`[EnglishWritingDataService] ${methodName}: Supabase fallback success`);
            return result;
          } catch (supabaseError) {
            this.logError(`${methodName}: Supabase fallback failed`, supabaseError);
            throw supabaseError;
          }
        }
        
        throw error;
      }
    }
    
    // Use Supabase directly if A2A is disabled
    if (supabaseFallback) {
      console.warn(`[EnglishWritingDataService] ${methodName}: A2A disabled (useA2A=${this.useA2A}), using Supabase`);
      this.log(`${methodName}: A2A disabled, using Supabase`);
      return await supabaseFallback();
    }
    
    throw new Error(`${methodName}: A2A disabled and no Supabase fallback available`);
  }

  // ============================================================================
  // Writing Examples Methods
  // ============================================================================

  /**
   * Get writing examples
   * Try A2A, fallback to Supabase
   */
  async getWritingExamples(params = {}) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aEnglishWritingService.getWritingExamples(params);
        return result.examples || result.data || result;
      },
      // Supabase fallback
      async () => {
        if (params.category && params.category !== '全部') {
          return await supabaseServices.fetchExamplesByCategory(params.category);
        }
        return await supabaseServices.fetchExamples();
      },
      'getWritingExamples'
    );
  }

  /**
   * Get writing examples by category
   * Try A2A, fallback to Supabase
   */
  async getWritingExamplesByCategory(category) {
    // Always log (even in production) for debugging
    console.log(`[EnglishWritingDataService] getWritingExamplesByCategory called`, {
      category,
      useA2A: this.useA2A,
      fallbackToSupabase: this.fallbackToSupabase
    });
    
    try {
      const result = await this.getWritingExamples({ category });
      console.log(`[EnglishWritingDataService] getWritingExamplesByCategory success`, {
        category,
        resultCount: Array.isArray(result) ? result.length : (result?.examples?.length || 0)
      });
      return result;
    } catch (error) {
      console.error(`[EnglishWritingDataService] getWritingExamplesByCategory error`, {
        category,
        error: error.message
      });
      throw error;
    }
  }

  /**
   * Get language data
   * Currently only available from Supabase
   */
  async getLanguageData() {
    return await supabaseServices.fetchLanguageData();
  }

  // ============================================================================
  // Sentence Expansion Methods (via DeepSeek AI)
  // ============================================================================

  /**
   * Expand a sentence using DeepSeek AI
   * A2A only (no Supabase fallback for AI operations)
   */
  async expandSentence(keyword, options = {}) {
    if (this.useA2A) {
      try {
        console.log(`[EnglishWritingDataService] expandSentence: Using A2A backend`);
        const result = await a2aEnglishWritingService.expandSentence(keyword, options);
        console.log(`[EnglishWritingDataService] expandSentence: A2A success`);
        // Return the full result object (component expects expandedSentences at top level)
        return result;
      } catch (error) {
        console.error(`[EnglishWritingDataService] expandSentence: A2A failed`, error);
        throw error;
      }
    }
    
    throw new Error('expandSentence: A2A backend is required for sentence expansion');
  }

  // ============================================================================
  // Composition Methods
  // ============================================================================

  /**
   * Save a composition
   * Try A2A, fallback to Supabase
   */
  async saveComposition(compositionData) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aEnglishWritingService.saveComposition(compositionData);
        return result.composition || result.data || result;
      },
      // Supabase fallback
      async () => {
        // Transform compositionData to match Supabase format
        const nodes = compositionData.nodes_data || compositionData.nodes || [];
        return await supabaseServices.saveCompositionToSupabase({
          title: compositionData.title,
          style: compositionData.style || 'composition',
          wordLength: compositionData.word_length || compositionData.wordLength || 0,
          finalCompositionText: compositionData.content || compositionData.final_composition_text,
          nodes: nodes.map(node => ({
            name: node.node_name || node.name,
            selectedSentences: (node.selected_sentences || node.selectedSentences || []).map(s => ({
              step_label: s.step_label || s.label,
              sentence_content: s.sentence_content || s.content
            }))
          }))
        });
      },
      'saveComposition'
    );
  }

  /**
   * Get compositions
   * Try A2A, fallback to Supabase
   */
  async getCompositions(params = {}) {
    const transformComposition = (comp) => {
      // Handle nodes_data - can be JSONB string, array, or null
      let nodesData = comp.nodes_data || comp.nodesData;
      if (typeof nodesData === 'string') {
        try {
          nodesData = JSON.parse(nodesData);
        } catch (e) {
          console.warn('[EnglishWritingDataService] Failed to parse nodes_data as JSON:', e);
          nodesData = [];
        }
      }
      if (!Array.isArray(nodesData)) {
        nodesData = nodesData ? [nodesData] : [];
      }

      // Always log (even in production) for debugging
      console.log('[EnglishWritingDataService] Transforming composition:', {
        id: comp.id || comp.composition_id,
        title: comp.title,
        hasNodesData: !!nodesData,
        nodesDataLength: nodesData.length,
        nodesDataType: typeof (comp.nodes_data || comp.nodesData),
        rawNodesData: comp.nodes_data || comp.nodesData
      });

      // Transform snake_case from database to camelCase for frontend
      return {
        id: comp.id || comp.composition_id,
        title: comp.title,
        style: comp.style,
        wordLength: comp.word_length || comp.wordLength,
        finalCompositionText: comp.final_composition_text || comp.finalCompositionText || comp.content,
        nodesData: nodesData,
        category: comp.category,
        userId: comp.user_id || comp.userId,
        createdAt: comp.created_at || comp.createdAt,
        updatedAt: comp.updated_at || comp.updatedAt
      };
    };

    return await this.tryWithFallback(
      // A2A
      async () => {
        const result = await a2aEnglishWritingService.getCompositions(params);
        const compositions = result.compositions || result.data || result;
        
        // Ensure it's an array and transform each composition
        const compositionsArray = Array.isArray(compositions) ? compositions : [];
        return compositionsArray.map(transformComposition);
      },
      // Supabase fallback
      async () => {
        const compositions = await supabaseServices.fetchCompositions();
        // Filter by user_id if provided
        let filtered = compositions;
        if (params.user_id) {
          filtered = compositions.filter(c => c.user_id === params.user_id);
        }
        // Transform each composition
        return filtered.map(transformComposition);
      },
      'getCompositions'
    );
  }

  /**
   * Save AI-generated example
   * Currently only available via Supabase
   */
  async saveExample(exampleData) {
    return await supabaseServices.saveExampleToSupabase(exampleData);
  }
}

// Export singleton instance
const englishWritingDataService = new EnglishWritingDataService();
export default englishWritingDataService;

